// frontend/src/pages/LocationRiskIndex.tsx
// Location Risk Index - Interactive Dashboard (Light Theme)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Button, Paper, Card, CardContent, TextField,
  Chip, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TablePagination, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Checkbox, ListItemText,
  OutlinedInput, Divider, Tooltip, LinearProgress, Collapse,
  Skeleton, Alert, IconButton,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Search as SearchIcon, Download as DownloadIcon, FilterList as FilterListIcon,
  Clear as ClearIcon, Map as MapIcon, LocationCity as LocationCityIcon,
  Place as PlaceIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Shield as ShieldIcon, AttachMoney as MoneyIcon, Refresh as RefreshIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface TierInfo {
  tier: string;
  count: number;
  label: string;
  color: string;
}

interface SummaryData {
  totalLocations: number;
  totalValue: number;
  avgRisk: number;
  avgResilience: number;
  totalEAL: number;
  tierDistribution: TierInfo[];
}

interface FilterOptions {
  states: string[];
  hazards: string[];
}

interface LocationDetail {
  postalCode: string;
  city: string;
  county: string;
  state: string;
  ravs: number;
  riskScore: number;
  eal: number;
  resilience: number;
  buildingValue: number;
  population: number;
  topHazard: string;
  nationalPercentile: number;
  statePercentile: number;
  hazardBreakdown: { name: string; score: number; eal: number }[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TIER_COLORS: Record<string, string> = {
  'A+': '#1B5E20', 'A': '#388E3C', 'B': '#7CB342',
  'C': '#FDD835', 'D': '#FF9800', 'F': '#EF4444',
};

const TIER_BG_COLORS: Record<string, string> = {
  'A+': '#E8F5E9', 'A': '#E8F5E9', 'B': '#F1F8E9',
  'C': '#FFFDE7', 'D': '#FFF3E0', 'F': '#FEE2E2',
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const TierBadge: React.FC<{ tier: string }> = ({ tier }) => (
  <Chip
    label={tier}
    size="small"
    sx={{
      fontWeight: 700,
      bgcolor: TIER_BG_COLORS[tier] || '#f5f5f5',
      color: TIER_COLORS[tier] || '#666',
      border: `2px solid ${TIER_COLORS[tier] || '#ccc'}`,
      minWidth: 40,
    }}
  />
);

const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}> = ({ title, value, subtitle, icon, color = '#00D4FF' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ 
          width: 48, height: 48, borderRadius: 2, 
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: `${color}15`
        }}>
          <Box sx={{ color }}>{icon}</Box>
        </Box>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B7280' }}>{title}</Typography>
      {subtitle && <Typography variant="caption" sx={{ color: '#9CA3AF' }}>{subtitle}</Typography>}
    </CardContent>
  </Card>
);

const TierDistributionBar: React.FC<{ distribution: TierInfo[] }> = ({ distribution }) => {
  const total = distribution.reduce((sum, t) => sum + t.count, 0);
  if (total === 0) return null;
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', height: 24, borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
        {distribution.map((tier) => (
          <Tooltip key={tier.tier} title={`${tier.tier}: ${tier.count.toLocaleString()} (${((tier.count / total) * 100).toFixed(1)}%)`}>
            <Box sx={{ width: `${(tier.count / total) * 100}%`, bgcolor: tier.color, '&:hover': { opacity: 0.8 }, transition: 'opacity 0.2s' }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {distribution.map((tier) => (
          <Box key={tier.tier} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: tier.color }} />
            <Typography variant="body2" sx={{ color: '#6B7280' }}>{tier.tier}: {tier.count.toLocaleString()}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function LocationRiskIndex() {
  const [viewType, setViewType] = useState<'postal-codes' | 'cities' | 'counties'>('postal-codes');
  const [season, setSeason] = useState('annual');
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('ravs');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [selectedCitySizes, setSelectedCitySizes] = useState<string[]>([]);
  const [selectedHazard, setSelectedHazard] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [maxRisk, setMaxRisk] = useState('');
  const [minResilience, setMinResilience] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);

  const formatCurrency = (value: number) => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  };

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await axios.get(`${API_URL}/index/filters`);
        if (response.data.success) setFilterOptions(response.data.data);
      } catch (err) { console.error('Failed to fetch filters:', err); }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const response = await axios.get(`${API_URL}/index/summary`, { params: { viewType, season } });
        if (response.data.success) setSummary(response.data.data);
      } catch (err) { console.error('Failed to fetch summary:', err); }
      finally { setSummaryLoading(false); }
    };
    fetchSummary();
  }, [viewType, season]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, any> = { season, sortBy, sortOrder, page: page + 1, limit: rowsPerPage };
      if (selectedStates.length > 0) params.state = selectedStates.join(',');
      if (selectedTiers.length > 0) params.tier = selectedTiers.join(',');
      if (selectedCitySizes.length > 0 && viewType === 'cities') params.citySize = selectedCitySizes.join(',');
      if (selectedHazard) params.topHazard = selectedHazard;
      if (searchQuery) params.search = searchQuery;
      if (maxRisk) params.maxRisk = maxRisk;
      if (minResilience) params.minResilience = minResilience;

      const endpoint = viewType === 'postal-codes' ? 'postal-codes' : viewType === 'cities' ? 'cities' : 'counties';
      const response = await axios.get(`${API_URL}/index/${endpoint}`, { params });
      
      if (response.data.success) {
        setData(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally { setLoading(false); }
  }, [viewType, season, sortBy, sortOrder, page, rowsPerPage, selectedStates, selectedTiers, selectedCitySizes, selectedHazard, searchQuery, maxRisk, minResilience]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchLocationDetail = async (postalCode: string) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API_URL}/index/location/${postalCode}`);
      if (response.data.success) { setSelectedLocation(response.data.data); setDetailOpen(true); }
    } catch (err) { console.error('Failed to fetch location detail:', err); }
    finally { setDetailLoading(false); }
  };

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: string | null) => {
    if (newView) { setViewType(newView as typeof viewType); setPage(0); }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortOrder('desc'); }
    setPage(0);
  };

  const handleClearFilters = () => {
    setSelectedStates([]); setSelectedTiers([]); setSelectedCitySizes([]);
    setSelectedHazard(''); setSearchQuery(''); setMaxRisk(''); setMinResilience(''); setPage(0);
  };

  const handleExport = () => {
    const headers = viewType === 'postal-codes' 
      ? ['Rank', 'Postal Code', 'City', 'County', 'State', 'Tier', 'RAVS', 'Risk', 'EAL', 'Resilience', 'Value', 'Population', 'Top Hazard']
      : viewType === 'cities'
      ? ['Rank', 'City', 'State', 'Tier', 'Size', 'Postal Codes', 'RAVS', 'Risk', 'EAL', 'Resilience', 'Value', 'Population', 'Top Hazard']
      : ['Rank', 'County', 'State', 'Tier', 'Postal Codes', 'Cities', 'RAVS', 'Risk', 'EAL', 'Resilience', 'Value', 'Population', 'Top Hazard'];
    const rows = data.map((row: any) => {
      if (viewType === 'postal-codes') return [row.rank, row.postalCode, row.city, row.county, row.state, row.tier, row.ravs?.toFixed(2), row.riskScore?.toFixed(2), row.eal?.toFixed(0), row.resilience?.toFixed(2), row.buildingValue?.toFixed(0), row.population, row.topHazard];
      if (viewType === 'cities') return [row.rank, row.city, row.state, row.tier, row.citySizeLabel, row.postalCodeCount, row.ravs?.toFixed(2), row.riskScore?.toFixed(2), row.eal?.toFixed(0), row.resilience?.toFixed(2), row.buildingValue?.toFixed(0), row.population, row.topHazard];
      return [row.rank, row.county, row.state, row.tier, row.postalCodeCount, row.cityCount, row.ravs?.toFixed(2), row.riskScore?.toFixed(2), row.eal?.toFixed(0), row.resilience?.toFixed(2), row.buildingValue?.toFixed(0), row.population, row.topHazard];
    });
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `location-risk-index-${viewType}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const activeFiltersCount = [selectedStates.length > 0, selectedTiers.length > 0, selectedCitySizes.length > 0, !!selectedHazard, !!maxRisk, !!minResilience].filter(Boolean).length;

  const renderTableHead = () => {
    const cellStyle = { fontWeight: 600, bgcolor: '#F9FAFB' };
    if (viewType === 'postal-codes') {
      return (
        <TableRow>
          <TableCell sx={cellStyle}>Rank</TableCell>
          <TableCell sx={cellStyle}><TableSortLabel active={sortBy === 'postalCode'} direction={sortBy === 'postalCode' ? sortOrder : 'asc'} onClick={() => handleSort('postalCode')}>Location</TableSortLabel></TableCell>
          <TableCell sx={cellStyle}>Tier</TableCell>
          <TableCell sx={cellStyle} align="right"><TableSortLabel active={sortBy === 'ravs'} direction={sortBy === 'ravs' ? sortOrder : 'asc'} onClick={() => handleSort('ravs')}>RAVS</TableSortLabel></TableCell>
          <TableCell sx={cellStyle} align="right"><TableSortLabel active={sortBy === 'risk'} direction={sortBy === 'risk' ? sortOrder : 'asc'} onClick={() => handleSort('risk')}>Risk</TableSortLabel></TableCell>
          <TableCell sx={cellStyle} align="right">EAL</TableCell>
          <TableCell sx={cellStyle} align="right">Resilience</TableCell>
          <TableCell sx={cellStyle} align="right">Value</TableCell>
          <TableCell sx={cellStyle}>Top Hazard</TableCell>
        </TableRow>
      );
    }
    if (viewType === 'cities') {
      return (
        <TableRow>
          <TableCell sx={cellStyle}>Rank</TableCell>
          <TableCell sx={cellStyle}>City</TableCell>
          <TableCell sx={cellStyle}>Size</TableCell>
          <TableCell sx={cellStyle}>Tier</TableCell>
          <TableCell sx={cellStyle} align="right">RAVS</TableCell>
          <TableCell sx={cellStyle} align="right">Risk</TableCell>
          <TableCell sx={cellStyle} align="right">EAL</TableCell>
          <TableCell sx={cellStyle} align="right">Value</TableCell>
          <TableCell sx={cellStyle} align="right">Population</TableCell>
          <TableCell sx={cellStyle}>Top Hazard</TableCell>
        </TableRow>
      );
    }
    return (
      <TableRow>
        <TableCell sx={cellStyle}>Rank</TableCell>
        <TableCell sx={cellStyle}>County</TableCell>
        <TableCell sx={cellStyle}>Tier</TableCell>
        <TableCell sx={cellStyle} align="center">Postal Codes</TableCell>
        <TableCell sx={cellStyle} align="center">Cities</TableCell>
        <TableCell sx={cellStyle} align="right">RAVS</TableCell>
        <TableCell sx={cellStyle} align="right">Risk</TableCell>
        <TableCell sx={cellStyle} align="right">Value</TableCell>
        <TableCell sx={cellStyle}>Top Hazard</TableCell>
      </TableRow>
    );
  };

  const renderTableRow = (row: any) => {
    if (viewType === 'postal-codes') {
      return (
        <TableRow key={row.postalCode} hover sx={{ cursor: 'pointer' }} onClick={() => fetchLocationDetail(row.postalCode)}>
          <TableCell>{row.rank}</TableCell>
          <TableCell><Box><Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{row.postalCode}</Typography><Typography variant="caption" sx={{ color: '#6B7280' }}>{row.city}, {row.state}</Typography></Box></TableCell>
          <TableCell><TierBadge tier={row.tier} /></TableCell>
          <TableCell align="right"><Typography sx={{ fontWeight: 600, color: '#00D4FF' }}>{row.ravs?.toFixed(1)}</Typography></TableCell>
          <TableCell align="right"><Typography sx={{ color: row.riskScore > 6 ? '#EF4444' : row.riskScore > 3 ? '#F59E0B' : '#10B981' }}>{row.riskScore?.toFixed(1)}</Typography></TableCell>
          <TableCell align="right">{formatCurrency(row.eal || 0)}</TableCell>
          <TableCell align="right">{row.resilience?.toFixed(1)}</TableCell>
          <TableCell align="right">{formatCurrency(row.buildingValue || 0)}</TableCell>
          <TableCell><Chip label={row.topHazard} size="small" variant="outlined" /></TableCell>
        </TableRow>
      );
    }
    if (viewType === 'cities') {
      return (
        <TableRow key={`${row.city}-${row.state}-${row.rank}`} hover>
          <TableCell>{row.rank}</TableCell>
          <TableCell><Box><Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{row.city}</Typography><Typography variant="caption" sx={{ color: '#6B7280' }}>{row.state} • {row.postalCodeCount} postal codes</Typography></Box></TableCell>
          <TableCell><Chip label={row.citySizeLabel} size="small" variant="outlined" /></TableCell>
          <TableCell><TierBadge tier={row.tier} /></TableCell>
          <TableCell align="right"><Typography sx={{ fontWeight: 600, color: '#00D4FF' }}>{row.ravs?.toFixed(1)}</Typography></TableCell>
          <TableCell align="right"><Typography sx={{ color: row.riskScore > 6 ? '#EF4444' : row.riskScore > 3 ? '#F59E0B' : '#10B981' }}>{row.riskScore?.toFixed(1)}</Typography></TableCell>
          <TableCell align="right">{formatCurrency(row.eal || 0)}</TableCell>
          <TableCell align="right">{formatCurrency(row.buildingValue || 0)}</TableCell>
          <TableCell align="right">{(row.population || 0).toLocaleString()}</TableCell>
          <TableCell><Chip label={row.topHazard} size="small" variant="outlined" /></TableCell>
        </TableRow>
      );
    }
    return (
      <TableRow key={`${row.county}-${row.state}-${row.rank}`} hover>
        <TableCell>{row.rank}</TableCell>
        <TableCell><Box><Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{row.county}</Typography><Typography variant="caption" sx={{ color: '#6B7280' }}>{row.state}</Typography></Box></TableCell>
        <TableCell><TierBadge tier={row.tier} /></TableCell>
        <TableCell align="center">{row.postalCodeCount}</TableCell>
        <TableCell align="center">{row.cityCount}</TableCell>
        <TableCell align="right"><Typography sx={{ fontWeight: 600, color: '#00D4FF' }}>{row.ravs?.toFixed(1)}</Typography></TableCell>
        <TableCell align="right"><Typography sx={{ color: row.riskScore > 6 ? '#EF4444' : row.riskScore > 3 ? '#F59E0B' : '#10B981' }}>{row.riskScore?.toFixed(1)}</Typography></TableCell>
        <TableCell align="right">{formatCurrency(row.buildingValue || 0)}</TableCell>
        <TableCell><Chip label={row.topHazard} size="small" variant="outlined" /></TableCell>
      </TableRow>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>Location Risk Index</Typography>
          <Typography variant="body1" sx={{ color: '#6B7280' }}>Risk-adjusted rankings for asset acquisition and portfolio monitoring</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh data"><IconButton onClick={fetchData} sx={{ backgroundColor: '#F3F4F6', '&:hover': { backgroundColor: '#E5E7EB' } }}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ backgroundColor: '#00D4FF', '&:hover': { backgroundColor: '#00B8E6' } }}>Export</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #E5E7EB', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <ToggleButtonGroup value={viewType} exclusive onChange={handleViewChange} size="small">
            <ToggleButton value="postal-codes"><PlaceIcon sx={{ mr: 1 }} /> Postal Codes</ToggleButton>
            <ToggleButton value="cities"><LocationCityIcon sx={{ mr: 1 }} /> Cities</ToggleButton>
            <ToggleButton value="counties"><MapIcon sx={{ mr: 1 }} /> Counties</ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Season</InputLabel>
            <Select value={season} onChange={(e) => { setSeason(e.target.value); setPage(0); }} label="Season">
              <MenuItem value="annual">Annual (All Hazards)</MenuItem>
              <MenuItem value="q1_winter">Q1 Winter (Jan-Mar)</MenuItem>
              <MenuItem value="q2_spring">Q2 Spring (Apr-Jun)</MenuItem>
              <MenuItem value="q3_summer">Q3 Summer (Jul-Sep)</MenuItem>
              <MenuItem value="q4_fall">Q4 Fall (Oct-Dec)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>{summaryLoading ? <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} /> : <StatCard title="Total Locations" value={summary?.totalLocations?.toLocaleString() || '0'} subtitle="Analyzed" icon={<PlaceIcon sx={{ fontSize: 28 }} />} />}</Grid>
        <Grid item xs={12} sm={6} md={3}>{summaryLoading ? <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} /> : <StatCard title="A+ Tier" value={summary?.tierDistribution?.find(t => t.tier === 'A+')?.count?.toLocaleString() || '0'} subtitle="Top 5%" icon={<StarIcon sx={{ fontSize: 28 }} />} color="#10B981" />}</Grid>
        <Grid item xs={12} sm={6} md={3}>{summaryLoading ? <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} /> : <StatCard title="Total Value" value={formatCurrency(summary?.totalValue || 0)} subtitle="Building value tracked" icon={<MoneyIcon sx={{ fontSize: 28 }} />} color="#F59E0B" />}</Grid>
        <Grid item xs={12} sm={6} md={3}>{summaryLoading ? <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} /> : <StatCard title="Avg Risk" value={(summary?.avgRisk || 0).toFixed(1)} subtitle="Out of 10" icon={<ShieldIcon sx={{ fontSize: 28 }} />} color={summary?.avgRisk && summary.avgRisk > 5 ? '#F59E0B' : '#10B981'} />}</Grid>
      </Grid>

      {summary?.tierDistribution && (
        <Card sx={{ mb: 3 }}><CardContent><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>Tier Distribution</Typography><TierDistributionBar distribution={summary.tierDistribution} /></CardContent></Card>
      )}

      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setFiltersExpanded(!filtersExpanded)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FilterListIcon sx={{ color: '#6B7280' }} /><Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>Filters</Typography>{activeFiltersCount > 0 && <Chip label={activeFiltersCount} size="small" color="primary" />}</Box>
          {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={filtersExpanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><TextField fullWidth size="small" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <SearchIcon sx={{ color: '#9CA3AF', mr: 1 }} /> }} /></Grid>
              <Grid item xs={12} md={4}><FormControl fullWidth size="small"><InputLabel>States</InputLabel><Select multiple value={selectedStates} onChange={(e) => setSelectedStates(e.target.value as string[])} input={<OutlinedInput label="States" />} renderValue={(selected) => selected.join(', ')}>{filterOptions?.states?.map((state) => (<MenuItem key={state} value={state}><Checkbox checked={selectedStates.includes(state)} /><ListItemText primary={state} /></MenuItem>))}</Select></FormControl></Grid>
              <Grid item xs={12} md={4}><FormControl fullWidth size="small"><InputLabel>Tiers</InputLabel><Select multiple value={selectedTiers} onChange={(e) => setSelectedTiers(e.target.value as string[])} input={<OutlinedInput label="Tiers" />} renderValue={(selected) => selected.join(', ')}>{['A+', 'A', 'B', 'C', 'D', 'F'].map((tier) => (<MenuItem key={tier} value={tier}><Checkbox checked={selectedTiers.includes(tier)} /><ListItemText primary={tier} /></MenuItem>))}</Select></FormControl></Grid>
              {viewType === 'cities' && (<Grid item xs={12} md={4}><FormControl fullWidth size="small"><InputLabel>City Size</InputLabel><Select multiple value={selectedCitySizes} onChange={(e) => setSelectedCitySizes(e.target.value as string[])} input={<OutlinedInput label="City Size" />} renderValue={(selected) => selected.join(', ')}><MenuItem value="large"><Checkbox checked={selectedCitySizes.includes('large')} /><ListItemText primary="Large (500K+)" /></MenuItem><MenuItem value="medium"><Checkbox checked={selectedCitySizes.includes('medium')} /><ListItemText primary="Medium (100K-500K)" /></MenuItem><MenuItem value="small"><Checkbox checked={selectedCitySizes.includes('small')} /><ListItemText primary="Small (50K-100K)" /></MenuItem></Select></FormControl></Grid>)}
              <Grid item xs={12} md={4}><FormControl fullWidth size="small"><InputLabel>Top Hazard</InputLabel><Select value={selectedHazard} onChange={(e) => setSelectedHazard(e.target.value)} label="Top Hazard"><MenuItem value="">All</MenuItem>{filterOptions?.hazards?.map((hazard) => (<MenuItem key={hazard} value={hazard}>{hazard}</MenuItem>))}</Select></FormControl></Grid>
              <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Max Risk" value={maxRisk} onChange={(e) => setMaxRisk(e.target.value)} type="number" inputProps={{ min: 0, max: 10, step: 0.1 }} /></Grid>
              <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Min Resilience" value={minResilience} onChange={(e) => setMinResilience(e.target.value)} type="number" inputProps={{ min: 0, max: 10, step: 0.1 }} /></Grid>
            </Grid>
            {activeFiltersCount > 0 && (<Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}><Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}>Clear All Filters</Button></Box>)}
          </Box>
        </Collapse>
      </Card>

      <Card>
        {loading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>{renderTableHead()}</TableHead>
            <TableBody>{data.map(renderTableRow)}{data.length === 0 && !loading && (<TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 4, color: '#6B7280' }}>No results found. Try adjusting your filters.</TableCell></TableRow>)}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={totalCount} page={page} onPageChange={(_, newPage) => setPage(newPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} />
      </Card>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #E5E7EB' }}>{selectedLocation ? `${selectedLocation.postalCode} - ${selectedLocation.city}, ${selectedLocation.state}` : 'Loading...'}</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {detailLoading ? (<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>) : selectedLocation && (
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}><Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#F0FDFA', borderRadius: 2 }}><Typography variant="caption" sx={{ color: '#6B7280' }}>RAVS Score</Typography><Typography variant="h4" sx={{ fontWeight: 700, color: '#00D4FF' }}>{selectedLocation.ravs?.toFixed(1)}</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#F9FAFB', borderRadius: 2 }}><Typography variant="caption" sx={{ color: '#6B7280' }}>Risk Score</Typography><Typography variant="h4" sx={{ fontWeight: 700, color: selectedLocation.riskScore > 6 ? '#EF4444' : selectedLocation.riskScore > 3 ? '#F59E0B' : '#10B981' }}>{selectedLocation.riskScore?.toFixed(1)}</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#F9FAFB', borderRadius: 2 }}><Typography variant="caption" sx={{ color: '#6B7280' }}>Resilience</Typography><Typography variant="h4" sx={{ fontWeight: 700, color: '#111827' }}>{selectedLocation.resilience?.toFixed(1)}</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#F9FAFB', borderRadius: 2 }}><Typography variant="caption" sx={{ color: '#6B7280' }}>EAL</Typography><Typography variant="h4" sx={{ fontWeight: 700, color: '#111827' }}>{formatCurrency(selectedLocation.eal || 0)}</Typography></Paper></Grid>
              <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Hazard Breakdown</Typography><Grid container spacing={1}>{selectedLocation.hazardBreakdown?.slice(0, 6).map((hazard) => (<Grid item xs={6} sm={4} key={hazard.name}><Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F9FAFB', borderRadius: 1 }}><Typography variant="caption" sx={{ color: '#6B7280' }}>{hazard.name}</Typography><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><Typography sx={{ fontWeight: 600, color: hazard.score > 5 ? '#EF4444' : '#111827' }}>{hazard.score?.toFixed(1)}</Typography><Typography variant="caption" sx={{ color: '#9CA3AF' }}>{formatCurrency(hazard.eal || 0)} EAL</Typography></Box><LinearProgress variant="determinate" value={(hazard.score || 0) * 10} sx={{ mt: 0.5, bgcolor: '#E5E7EB', '& .MuiLinearProgress-bar': { bgcolor: hazard.score > 5 ? '#EF4444' : '#00D4FF' } }} /></Paper></Grid>))}</Grid></Grid>
              <Grid item xs={12}><Divider sx={{ my: 1 }} /><Grid container spacing={2}><Grid item xs={6} sm={3}><Typography variant="caption" sx={{ color: '#6B7280' }}>Building Value</Typography><Typography variant="body1" sx={{ fontWeight: 600 }}>{formatCurrency(selectedLocation.buildingValue || 0)}</Typography></Grid><Grid item xs={6} sm={3}><Typography variant="caption" sx={{ color: '#6B7280' }}>Population</Typography><Typography variant="body1" sx={{ fontWeight: 600 }}>{(selectedLocation.population || 0).toLocaleString()}</Typography></Grid><Grid item xs={6} sm={3}><Typography variant="caption" sx={{ color: '#6B7280' }}>National Percentile</Typography><Typography variant="body1" sx={{ fontWeight: 600 }}>Top {(100 - (selectedLocation.nationalPercentile || 0)).toFixed(0)}%</Typography></Grid><Grid item xs={6} sm={3}><Typography variant="caption" sx={{ color: '#6B7280' }}>State Percentile</Typography><Typography variant="body1" sx={{ fontWeight: 600 }}>Top {(100 - (selectedLocation.statePercentile || 0)).toFixed(0)}%</Typography></Grid></Grid></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #E5E7EB', px: 3, py: 2 }}><Button onClick={() => setDetailOpen(false)}>Close</Button><Button variant="contained" sx={{ bgcolor: '#00D4FF', '&:hover': { bgcolor: '#00B8E6' } }}>Add to Portfolio</Button></DialogActions>
      </Dialog>
    </Container>
  );
}
